import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { Combobox } from '@/components/ui/combobox';
import { useCreateUser } from '@/hooks/use-users';
import { useMerchants } from '@/hooks/use-merchants';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useAuth } from '@/hooks/use-auth';
import { ApiError } from '@/lib/api/client';
import type { CreateUserRequest } from '@/types/user';

const ROLE_OPTIONS = [
  { value: 'platform_admin', label: 'Platform Admin' },
  { value: 'platform_it', label: 'Platform IT' },
  { value: 'platform_ops', label: 'Platform Ops' },
  { value: 'merchant_product_manager', label: 'Merchant Product Manager' },
  { value: 'merchant_it', label: 'Merchant IT' },
  { value: 'merchant_viewer', label: 'Merchant Viewer' },
] as const;

/** Rollen, die einen Merchant-Bezug erfordern */
const MERCHANT_ROLES = new Set(['merchant_product_manager', 'merchant_it', 'merchant_viewer']);

/** Spec 0221 §4: Ceiling — ein merchant_product_manager darf nur Merchant-Rollen vergeben. */
const MERCHANT_ROLE_OPTIONS = ROLE_OPTIONS.filter((o) => MERCHANT_ROLES.has(o.value));

/** Extrahiert die (lowercase) Domain einer E-Mail; '' wenn keine vorhanden. */
function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0 ? '' : email.slice(at + 1).trim().toLowerCase();
}

export function UserCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('users');
  const createMutation = useCreateUser();

  // Spec 0220/0221: showMerchant = Platform-Rolle (waehlt Merchant), ownMerchantId = eigener Tenant.
  const { showMerchant, ownMerchantId } = useMerchantScope();
  const { user } = useAuth();
  const ownDomain = emailDomain(user?.email ?? '');

  // Spec 0221 §12.10: Merchant-PM darf nur in die eigene Domain einladen → Default-Rolle viewer.
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(showMerchant ? '' : 'merchant_viewer');
  const [merchantId, setMerchantId] = useState('');

  const isMerchantRole = MERCHANT_ROLES.has(role);

  // Rollen-Dropdown: Platform sieht alle, Merchant-PM nur das Ceiling (Merchant-Rollen).
  const roleOptions = showMerchant ? ROLE_OPTIONS : MERCHANT_ROLE_OPTIONS;

  // Merchant-Combobox nur fuer Platform-Inviter mit Merchant-Ziel-Rolle; Merchant-PM bekommt
  // den eigenen Tenant erzwungen und sieht das Feld nicht.
  const showMerchantPicker = showMerchant && isMerchantRole;
  // Spec 0221 §B3: Merchant-PM braucht die Merchant-Liste nicht → Fetch unterdruecken.
  const { data: merchantData, isLoading: merchantsLoading } = useMerchants(
    { limit: 200 },
    { enabled: showMerchant },
  );

  const merchantOptions = (merchantData?.data ?? []).map((m) => ({
    value: m.merchantId,
    label: m.name,
    description: m.merchantId,
  }));

  // Domain-Lock (nur Merchant-PM): Client-Spiegel der Server-Regel (Server bleibt die harte Grenze).
  const domainMismatch =
    !showMerchant && email.trim().length > 0 && !!ownDomain && emailDomain(email) !== ownDomain;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: CreateUserRequest = {
      email: email.trim(),
      role,
    };
    if (showMerchant) {
      // Platform-Inviter: Merchant aus der Combobox (nur bei Merchant-Rollen).
      if (isMerchantRole && merchantId) payload.merchantId = merchantId;
    } else if (ownMerchantId) {
      // Merchant-PM: eigener Tenant erzwungen (UI; Server erzwingt es ebenfalls).
      payload.merchantId = ownMerchantId;
    }

    try {
      await createMutation.mutateAsync(payload);
      toast.success(t('create.toast.created'));
      navigate('/users');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          // Spec 0221 §12.8: Merchant-Inviter bekommt die generische Server-Meldung (kein
          // Tenant-Leak ueber "existiert bereits"); Platform sieht die konkrete Meldung.
          toast.error(showMerchant
            ? t('create.toast.alreadyExists')
            : (err.message || t('create.toast.inviteFailed')));
        } else if (err.status === 429) {
          toast.error(t('create.toast.tooManyRequests'));
        } else if (err.status === 400) {
          const detail = err.details?.map((d) => `${d.field}: ${d.message}`).join(', ');
          toast.error(detail ?? err.message);
        } else {
          toast.error(err.message);
        }
      } else {
        toast.error(String(err));
      }
    }
  };

  const isValid =
    email.trim().length > 0 &&
    role.length > 0 &&
    !domainMismatch &&
    (showMerchantPicker ? merchantId.length > 0 : true);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {showMerchant ? t('create.heading.newUser') : t('create.heading.inviteTeamMember')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {showMerchant
              ? t('create.subtitle.newUser')
              : t('create.subtitle.team')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('create.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6 max-w-lg">
            {/* E-Mail */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('create.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                required
                placeholder={ownDomain && !showMerchant
                  ? t('create.emailPlaceholderOwnDomain', { domain: ownDomain })
                  : t('create.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                aria-invalid={domainMismatch}
              />
              {!showMerchant && ownDomain && (
                <p className={`text-xs ${domainMismatch ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {domainMismatch
                    ? t('create.domainMismatch', { domain: ownDomain })
                    : t('create.domainHint', { domain: ownDomain })}
                </p>
              )}
            </div>

            {/* Rolle */}
            <div className="space-y-2">
              <Label htmlFor="role">{t('create.roleLabel')}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="role">
                  <SelectValue placeholder={t('create.rolePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Merchant-ID — nur fuer Platform-Inviter mit Merchant-Ziel-Rolle */}
            {showMerchantPicker && (
              <div className="space-y-2">
                <Label htmlFor="merchantId">{t('create.merchantLabel')}</Label>
                <Combobox
                  id="merchantId"
                  options={merchantOptions}
                  value={merchantId}
                  onChange={setMerchantId}
                  placeholder={t('create.merchantPlaceholder')}
                  searchPlaceholder={t('create.merchantSearchPlaceholder')}
                  emptyText={t('create.merchantEmpty')}
                  loading={merchantsLoading}
                />
                <p className="text-xs text-muted-foreground">
                  {t('create.merchantAssignHint')}
                </p>
              </div>
            )}

            {/* Hinweis */}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
              {t('create.invitationHint')}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!isValid || createMutation.isPending}
              >
                {t('create.submit')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
